import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, FormControl,
  FormControlLabel, FormLabel, Paper, Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import { CheckCircleOutline, SaveOutlined, ShieldOutlined } from '@mui/icons-material';
import api from '../axiosConfig';
import { UserContext } from '../UserContext';
import { useBranding } from '../BrandingContext';
import { getRegisterBrand, normalizeRegisterTheme, REGISTER_THEMES } from '../registerBranding';

const themeName = (theme) => (theme === 'default' ? 'Default EXREG' : getRegisterBrand(theme).name);
const themeDescriptions = {
  default: 'EXREG appearance and your uploaded register logo.',
  orlen: 'ORLEN red, white and charcoal, with the official logo.',
  'bumi-armada': 'Bumi Armada navy and green, with the official logo.',
};
const errorMessage = (error, fallback) => {
  const message = error.response?.data?.error || error.response?.data?.message;
  return typeof message === 'string' ? message : fallback;
};

function ThemePreview({ theme, database, logoUrl }) {
  const brand = getRegisterBrand(theme);
  const branded = brand.key !== 'default';
  const accent = brand.primary;
  const customLogo = logoUrl ? `${process.env.REACT_APP_API_BASE_URL || ''}${logoUrl}` : '';

  return (
    <Paper variant="outlined" sx={{ minWidth: 0, overflow: 'hidden', borderRadius: 3, bgcolor: '#f5f6f8' }}>
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, bgcolor: '#fff', borderBottom: '1px solid #e3e5e8' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="overline" sx={{ color: '#5f6c7b', fontSize: { xs: 10, sm: 12 }, fontWeight: 800, letterSpacing: '0.13em' }}>
            Theme preview
          </Typography>
          <Chip size="small" label={branded ? `${brand.name} × EXREG` : 'EXREG'} sx={{ maxWidth: '100%', fontSize: { xs: 11, sm: 13 }, bgcolor: brand.soft, color: accent }} />
        </Stack>
      </Box>
      <Box sx={{ bgcolor: brand.navBackground, p: { xs: 2, sm: 3 }, borderBottom: `4px solid ${brand.accent}`, color: '#fff' }}>
        <Stack direction="row" alignItems="center" sx={{ mb: { xs: 3, sm: 4 }, gap: { xs: 1.25, sm: 2 }, flexWrap: 'wrap' }}>
          {branded ? (
            <Box component="img" src={brand.logoUrl} alt={brand.name} sx={{ width: brand.logoAspect === 'wide' ? { xs: 120, sm: 160 } : 66, height: brand.logoAspect === 'wide' ? { xs: 54, sm: 66 } : 66, maxWidth: '100%', objectFit: 'contain', bgcolor: '#fff', p: brand.logoAspect === 'wide' ? 1 : 0, borderRadius: brand.logoAspect === 'wide' ? 1 : 0 }} />
          ) : customLogo ? (
            <Box component="img" src={customLogo} alt="Register logo" sx={{ maxWidth: 120, height: 54, objectFit: 'contain', bgcolor: '#fff', borderRadius: 1, p: 0.5 }} />
          ) : null}
          {(branded || customLogo) && <Typography sx={{ color: '#a5a9b1', fontSize: { xs: 18, sm: 22 } }}>×</Typography>}
          <Typography sx={{ fontSize: { xs: 15, sm: 18 }, fontWeight: 900, letterSpacing: '0.12em' }}>EXREG</Typography>
        </Stack>
        <Typography sx={{ color: '#ccd0d7', fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', mb: 1 }}>
          EQUIPMENT & INSPECTION MANAGEMENT
        </Typography>
        <Typography component="h3" sx={{ fontWeight: 800, fontSize: { xs: 24, md: 29 }, letterSpacing: '-0.025em', lineHeight: 1.15 }}>
          {branded ? brand.name : 'EXREG workspace'}
        </Typography>
        <Typography sx={{ color: '#b9bec7', mt: 1, fontSize: 13, overflowWrap: 'anywhere' }}>
          {database || 'Your selected register'}
        </Typography>
      </Box>
      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction="row" sx={{ mb: 2.5, gap: 1, flexWrap: 'wrap' }} aria-hidden="true">
          {['Overview', 'Equipment', 'Inspections'].map((label, index) => (
            <Box key={label} sx={{ px: { xs: 1, sm: 1.5 }, py: 0.75, borderRadius: 1, fontWeight: 700, fontSize: 11, bgcolor: index === 0 ? accent : '#fff', color: index === 0 ? '#fff' : '#606873' }}>
              {label}
            </Box>
          ))}
        </Stack>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: '#e3e5e8' }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
            <ShieldOutlined sx={{ color: accent }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: '#242931', fontSize: 13, fontWeight: 800 }}>Equipment register</Typography>
              <Typography sx={{ fontSize: 11, color: '#6b7280' }}>A clear view of asset integrity</Typography>
            </Box>
          </Stack>
          {[82, 64, 73].map((width, index) => (
            <Stack key={width} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1.1, borderTop: '1px solid #edf0f3' }} aria-hidden="true">
              <Box sx={{ height: 6, width: `${width - 25}%`, bgcolor: '#e4e7eb', borderRadius: 1 }} />
              <Box sx={{ height: 16, width: 49, bgcolor: index === 1 ? '#fff3e5' : '#eaf5ed', borderRadius: 2 }} />
            </Stack>
          ))}
        </Paper>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, gap: 1, flexWrap: 'wrap' }}>
          <Stack direction="row" spacing={0.7} aria-label="Theme colors">
            {[...new Set([accent, brand.accent, brand.navBackground, '#ffffff'])].map((color) => (
              <Box key={color} sx={{ width: 17, height: 17, borderRadius: '50%', bgcolor: color, border: '1px solid rgba(0,0,0,0.1)' }} />
            ))}
          </Stack>
          <Typography sx={{ color: '#69717e', fontSize: 10, fontWeight: 600 }}>Powered by EXREG</Typography>
        </Stack>
      </Box>
    </Paper>
  );
}

export default function RegisterThemes() {
  const { user } = useContext(UserContext);
  const { refreshBranding } = useBranding();
  const activeDatabase = user?.database_name || '';
  const activeDatabaseRef = useRef(activeDatabase);
  activeDatabaseRef.current = activeDatabase;
  const mounted = useRef(false);
  const loadSequence = useRef(0);
  const [databases, setDatabases] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [listRetry, setListRetry] = useState(0);
  const [selectedDatabase, setSelectedDatabase] = useState('');
  const [saved, setSaved] = useState(null);
  const [draftTheme, setDraftTheme] = useState('default');
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [brandingError, setBrandingError] = useState('');
  const [brandingRetry, setBrandingRetry] = useState(0);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setListLoading(true);
    setListError('');
    api.get('/api/admin/databases', { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        const names = data
          .map(({ datname }) => datname)
          .filter((name) => name && !['postgres', 'user_management', 'template0', 'template1'].includes(name))
          .sort((a, b) => a.localeCompare(b));
        setDatabases(names);
        setSelectedDatabase((previous) => names.includes(previous) ? previous : names.includes(activeDatabaseRef.current) ? activeDatabaseRef.current : names[0] || '');
      })
      .catch((error) => {
        if (!controller.signal.aborted) setListError(errorMessage(error, 'Could not load registers. Please try again.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setListLoading(false);
      });
    return () => controller.abort();
  }, [listRetry]);

  useEffect(() => {
    if (!selectedDatabase) return undefined;
    const controller = new AbortController();
    const sequence = ++loadSequence.current;
    setSaved(null);
    setBrandingLoading(true);
    setBrandingError('');
    setNotice(null);
    api.get(`/api/settings/database/${encodeURIComponent(selectedDatabase)}/branding`, { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted || sequence !== loadSequence.current) return;
        const theme = normalizeRegisterTheme(data.theme);
        setSaved({ ...data, theme, database: selectedDatabase });
        setDraftTheme(theme);
      })
      .catch((error) => {
        if (!controller.signal.aborted && sequence === loadSequence.current) {
          setBrandingError(errorMessage(error, 'Could not load this register’s theme. Please try again.'));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted && sequence === loadSequence.current) setBrandingLoading(false);
      });
    return () => controller.abort();
  }, [selectedDatabase, brandingRetry]);

  const ready = saved?.database === selectedDatabase && !brandingLoading && !brandingError;
  const dirty = ready && draftTheme !== saved.theme;

  const saveTheme = async () => {
    if (!dirty || saving) return;
    const database = selectedDatabase;
    const theme = draftTheme;
    setSaving(true);
    setNotice(null);
    try {
      const { data } = await api.put(`/api/settings/database/${encodeURIComponent(database)}/branding`, { theme });
      if (!mounted.current) return;
      setSaved((previous) => ({ ...previous, ...data, database, theme }));
      setNotice({ severity: 'success', text: `${themeName(theme)} theme saved for ${database}.` });
      if (database === activeDatabaseRef.current) {
        try {
          const refreshed = await refreshBranding();
          if (refreshed === false && mounted.current) {
            setNotice({ severity: 'warning', text: 'Theme saved. Reload the register to refresh its appearance.' });
          }
        } catch {
          if (mounted.current) setNotice({ severity: 'warning', text: 'Theme saved. Reload the register to refresh its appearance.' });
        }
      }
    } catch (error) {
      if (mounted.current) setNotice({ severity: 'error', text: errorMessage(error, 'Could not save the theme. Your changes are still available to retry.') });
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0, pb: 3 }}>
      {listError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => setListRetry((value) => value + 1)}>Retry</Button>}>{listError}</Alert>}
      {notice && <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 3, alignItems: 'start' }}>
        <Paper variant="outlined" sx={{ minWidth: 0, p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
          <Typography component="h2" variant="h6" sx={{ mb: 0.5 }}>Register appearance</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Theme settings apply only to the register selected below.
          </Typography>
          <TextField
            select
            fullWidth
            id="theme-register"
            label="Register"
            value={selectedDatabase}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            disabled={listLoading || saving || Boolean(listError) || databases.length === 0}
            onChange={(event) => {
              loadSequence.current += 1;
              setSaved(null);
              setNotice(null);
              setSelectedDatabase(event.target.value);
            }}
            helperText={selectedDatabase === activeDatabase ? 'This is your currently connected register.' : 'Your currently connected register will stay the same.'}
          >
            {databases.length === 0 && <option value="">{listLoading ? 'Loading registers…' : 'No registers available'}</option>}
            {databases.map((database) => <option key={database} value={database}>{database}</option>)}
          </TextField>

          {(listLoading || brandingLoading) && <Stack direction="row" spacing={1} alignItems="center" role="status" sx={{ my: 3 }}><CircularProgress size={18} /><Typography variant="body2">Loading {listLoading ? 'registers' : 'theme'}…</Typography></Stack>}
          {!listLoading && !listError && databases.length === 0 && <Alert severity="info" sx={{ mt: 3 }}>Create a register in Database Management before configuring its theme.</Alert>}
          {brandingError && <Alert severity="error" sx={{ mt: 3 }} action={<Button color="inherit" onClick={() => setBrandingRetry((value) => value + 1)}>Retry</Button>}>{brandingError}</Alert>}

          <FormControl fullWidth disabled={!ready || saving} sx={{ mt: 3 }}>
            <FormLabel id="register-theme-options-label" sx={{ mb: 1.25, fontSize: 14, fontWeight: 600 }}>Theme</FormLabel>
            <RadioGroup
              aria-labelledby="register-theme-options-label"
              name="register-theme"
              value={ready ? draftTheme : ''}
              onChange={(event) => { setDraftTheme(event.target.value); setNotice(null); }}
              sx={{ gap: 1.25 }}
            >
              {REGISTER_THEMES.map((brand) => {
                const selected = ready && draftTheme === brand.key;
                return (
                  <FormControlLabel
                    key={brand.key}
                    value={brand.key}
                    sx={{ m: 0, p: 1.5, gap: 1.25, border: '1px solid', borderColor: selected ? brand.primary : 'divider', borderRadius: 2, bgcolor: selected ? brand.soft : '#fff', alignItems: 'center', '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 } }}
                    control={<Radio sx={{ p: 0.5, color: '#8993a0', '&.Mui-checked': { color: brand.primary } }} inputProps={{ 'aria-label': themeName(brand.key), 'aria-describedby': `theme-description-${brand.key}` }} />}
                    label={(
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        {brand.key === 'default' ? (
                          <Box sx={{ width: 62, height: 48, flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: 1, bgcolor: '#0b1220', color: '#fff', fontSize: 10, fontWeight: 900, letterSpacing: '0.05em' }}>EXREG</Box>
                        ) : (
                          <Box component="img" src={brand.logoUrl} alt="" sx={{ width: 62, height: 48, flexShrink: 0, objectFit: 'contain', bgcolor: '#fff', borderRadius: 0.5, p: brand.logoAspect === 'wide' ? 0.25 : 0 }} />
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{themeName(brand.key)}</Typography>
                          <Typography id={`theme-description-${brand.key}`} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.5 }}>{themeDescriptions[brand.key]}</Typography>
                        </Box>
                      </Stack>
                    )}
                  />
                );
              })}
            </RadioGroup>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.65 }}>
            Choose Default EXREG to restore your uploaded register logo. Your register data stays the same.
          </Typography>
          <Divider sx={{ my: 3 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2}>
            <Box aria-live="polite">
              {ready && <Chip size="small" icon={dirty ? undefined : <CheckCircleOutline />} label={dirty ? 'Unsaved changes' : `${themeName(saved.theme)} saved`} variant="outlined" color={dirty ? 'warning' : 'default'} />}
            </Box>
            <Button variant="contained" startIcon={saving ? <CircularProgress color="inherit" size={17} /> : <SaveOutlined />} disabled={!dirty || saving || listLoading || Boolean(listError)} onClick={saveTheme}>
              {saving ? 'Saving…' : 'Save theme'}
            </Button>
          </Stack>
        </Paper>

        <Box sx={{ minWidth: 0 }}>
          {ready ? <ThemePreview theme={draftTheme} database={selectedDatabase} logoUrl={saved.logoUrl} /> : (
            <Paper variant="outlined" sx={{ minHeight: 370, display: 'grid', placeItems: 'center', p: 3, borderRadius: 3 }}>
              <Typography color="text.secondary" align="center">Select a register to preview its theme.</Typography>
            </Paper>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, px: 0.5 }}>
            Illustrative preview. Changes take effect after saving. Other registers keep their existing theme.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
